/**
 * `<product-form>` custom element.
 *
 * Wraps the product form and keeps it in sync with variant selection:
 * hidden variant id, price, availability, quantity rules, URL (preserving
 * existing query params such as `utm_*`, AD-27) and the active media.
 *
 * Dispatches `shopify:product:select` (AD-29) on variant change, and
 * `shopify:cart:lines-update` / `shopify:cart:error` on add-to-cart.
 */
class ProductForm extends AddToCartBase {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantJson = JSON.parse(this.querySelector('[data-variant-json]').textContent);
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.addToCartButton = this.querySelector('[data-add-to-cart]');
    this.addToCartText = this.querySelector('[data-add-to-cart-text]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.sellingPlanSelect = this.querySelector('select[name="selling_plan"]');
    this.statusEl = this.querySelector('[data-form-status]');
    this.successMessage = this.dataset.successMessage;
    this.currentVariant =
      this.variantJson.find((variant) => String(variant.id) === this.variantIdInput.value) ||
      this.variantJson[0];
  }

  connectedCallback() {
    // Since v1.3 the option selectors are their own block, in another column:
    // the scope is the whole product (the quick view injects its own
    // `.product`, so two products on the page never see each other).
    this.scope = this.closest('.product') || this;
    // `change` on the scope covers option inputs, quantity and selling plan
    // alike: every one of them re-resolves the variant and the price (AD-25).
    this.scope.addEventListener('change', this.onOptionChange.bind(this));
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    if (this.quantityInput) {
      // While typing, before `change` fires: the price follows the quantity.
      this.quantityInput.addEventListener('input', () => this.updatePrice(this.currentVariant));
    }
    // Liquid renders the base variant price; if the opening quantity already
    // sits on a price break, or a plan is preselected, align at once.
    if (this.currentVariant) this.updatePrice(this.currentVariant);
    // Same for the struck-through options: the first render knows nothing of
    // the combination the page opens on.
    this.updateOptionAvailability();
  }

  selectedOptions() {
    const byPosition = {};
    this.scope.querySelectorAll('[data-option-position]').forEach((input) => {
      const position = input.dataset.optionPosition;
      const fieldset = input.closest('fieldset');
      const legend = fieldset ? fieldset.querySelector('legend') : null;
      const name = legend ? legend.textContent.trim() : '';
      if (input.type === 'radio') {
        if (input.checked) byPosition[position] = { name, value: input.value };
      } else {
        byPosition[position] = { name, value: input.value };
      }
    });
    return Object.keys(byPosition)
      .sort((a, b) => a - b)
      .map((key) => byPosition[key]);
  }

  findVariant(options) {
    // Single-variant product: no option inputs, the only variant is the one.
    if (options.length === 0) return this.variantJson[0];
    const values = options.map((option) => option.value);
    return this.variantJson.find((variant) =>
      variant.options.every((value, index) => value === values[index])
    );
  }

  onOptionChange() {
    const options = this.selectedOptions();
    const variant = this.findVariant(options);
    if (!variant) return;
    this.updateVariant(variant);
    this.dispatchProductSelect(variant, options);
  }

  updateVariant(variant) {
    this.currentVariant = variant;
    this.variantIdInput.value = variant.id;
    this.updateQuantityRules(variant);
    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.updateOptionAvailability();
    this.updateUrl(variant);
    this.updateMedia(variant);
  }

  /**
   * The struck-through state Liquid renders means "this value exists in at
   * least one available variant", not "available with what is selected": with
   * a colour chosen, a size sold out *in that colour* renders clean and the
   * shopper only finds out from the button. Recomputed here on the selected
   * combination, at every change and once on load.
   *
   * Only the class is touched, never `disabled`: an unavailable value stays
   * selectable, which is how a shopper finds the colour that still has it.
   */
  updateOptionAvailability() {
    const selected = this.selectedOptions().map((option) => option.value);
    this.scope.querySelectorAll('input[data-option-position]').forEach((input) => {
      const label = input.closest('label');
      if (!label) return;
      const candidate = selected.slice();
      candidate[Number(input.dataset.optionPosition) - 1] = input.value;
      const variant = this.variantJson.find((entry) =>
        entry.options.every((value, index) => value === candidate[index])
      );
      const soldOut = !variant || !variant.available;
      if (label.classList.contains('variant-picker__size')) {
        label.classList.toggle('variant-picker__size--unavailable', soldOut);
      } else if (label.classList.contains('variant-picker__color-tile')) {
        label.classList.toggle('variant-picker__color-tile--unavailable', soldOut);
      }
    });
  }

  updateQuantityRules(variant) {
    if (!this.quantityInput) return;
    this.quantityInput.min = variant.quantity_rule.min;
    if (variant.quantity_rule.max) {
      this.quantityInput.max = variant.quantity_rule.max;
    } else {
      this.quantityInput.removeAttribute('max');
    }
    this.quantityInput.step = variant.quantity_rule.increment;
    if (parseInt(this.quantityInput.value, 10) < variant.quantity_rule.min) {
      this.quantityInput.value = variant.quantity_rule.min;
    }
  }

  /**
   * The price is not a number (AD-25): a selling plan has its own allocation,
   * and volume pricing picks the highest break whose minimum the chosen
   * quantity reaches. Falls back to the variant's own price.
   */
  effectivePrice(variant) {
    const planId = this.sellingPlanSelect ? this.sellingPlanSelect.value : '';
    const allocation = planId
      ? (variant.selling_plan_allocations || []).find((a) => String(a.selling_plan_id) === planId)
      : null;
    if (allocation) {
      return {
        price: allocation.price,
        price_money: allocation.price_money,
        compare_at_price: allocation.compare_at_price,
        compare_at_money: allocation.compare_at_money
      };
    }
    const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
    const breakHit = (variant.quantity_price_breaks || [])
      .filter((b) => b.minimum_quantity <= quantity)
      .sort((a, b) => b.minimum_quantity - a.minimum_quantity)[0];
    if (breakHit) {
      return {
        price: breakHit.price,
        price_money: breakHit.price_money,
        compare_at_price: variant.compare_at_price,
        compare_at_money: variant.compare_at_money
      };
    }
    return variant;
  }

  updatePrice(baseVariant) {
    if (!baseVariant) return;
    const variant = this.effectivePrice(baseVariant);
    const scope = this.closest('.product') || document;
    const price = scope.querySelector('[data-price]');
    if (!price) return;
    const amount = price.querySelector('[data-price-amount]');
    const compare = price.querySelector('[data-price-compare]');
    const setSale = (onSale) => {
      // Le etichette per lo screen reader seguono lo stato, non restano «Regular price» sullo scontato.
      price.querySelectorAll('[data-price-label-regular]').forEach((el) => { el.hidden = onSale; });
      price.querySelectorAll('[data-price-label-sale], [data-price-compare-label]').forEach((el) => { el.hidden = !onSale; });
    };
    if (amount) amount.textContent = variant.price_money;
    if (compare) {
      if (variant.compare_at_price > variant.price) {
        compare.hidden = false;
        compare.textContent = variant.compare_at_money;
        setSale(true);
        price.classList.add('price--on-sale');
        if (amount) amount.classList.add('price__amount--sale');
      } else {
        compare.hidden = true;
        setSale(false);
        price.classList.remove('price--on-sale');
        if (amount) amount.classList.remove('price__amount--sale');
      }
    }
  }

  updateAvailability(variant) {
    if (!this.addToCartButton || !this.addToCartText) return;
    if (variant.available) {
      this.addToCartButton.removeAttribute('disabled');
      this.addToCartText.textContent = this.addToCartButton.dataset.addToCartLabel;
    } else {
      this.addToCartButton.setAttribute('disabled', 'disabled');
      this.addToCartText.textContent = this.addToCartButton.dataset.soldOutLabel;
    }
  }

  updateUrl(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * The variant's own photo becomes the visible one: click its thumbnail, so
   * the gallery keeps a single place that decides what the stage shows. Ran
   * before the colour filter on purpose — the gallery keeps this choice as
   * long as the new colour leaves the photo visible.
   */
  updateMedia(variant) {
    if (!variant.featured_image_id) return;
    const thumb = this.scope.querySelector(
      `[data-gallery-thumb="${variant.featured_image_id}"]`
    );
    if (thumb) thumb.click();
  }

  dispatchProductSelect(variant, options) {
    const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';
    window.THCabina.events.dispatch('shopify:product:select', {
      context: 'product',
      selectedOptions: options,
      variant: {
        id: String(variant.id),
        title: variant.options.join(' / '),
        available: variant.available,
        price: { amount: variant.price_amount, currencyCode: currency }
      }
    });
  }

  onSubmit(event) {
    if (!event.submitter || event.submitter.name !== 'add') return;
    event.preventDefault();
    this.addToCart({
      context: 'product',
      lines: [{ merchandiseId: this.variantIdInput.value, quantity: this.quantityInput ? this.quantityInput.value : 1 }],
      body: new URLSearchParams(new FormData(this.form)).toString(),
      target: this.form,
      successMessage: this.successMessage,
      button: this.addToCartButton
    });
  }

  setStatus(message, isError) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.classList.toggle('product-form__status--error', Boolean(isError));
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
